<template>
    <div class="p-4 shadow-inner bg-slate-300/40 rounded-xl lg:pl-6">
        <div class="flex justify-between items-center">
            <div class="text-lg font-extrabold">
                {{ field.name }}
            </div>
            <div>
                <vx-button @click="emit('remove')" :disabled="props.disabled" data-test="remove-field" outline size="md" color="muted-dark" class="mr-2">
                    <font-awesome-icon widthAuto :icon="faTrash" />
                </vx-button>
                <vx-button @click="visible = !visible" size="md" color="muted-dark">
                    <font-awesome-icon widthAuto :icon="visible ? faChevronDown : faChevronUp" />
                </vx-button>
            </div>
        </div>
        <div v-if="visible" class="flex flex-col gap-3 pt-4">
            <div class="flex flex-col gap-2" data-test="field-description">
                <div class="flex items-center gap-4 ">
                    <vx-label class="grow">
                        Description
                        <template v-slot:description> Add a description with helper text and examples. </template>
                    </vx-label>
                    <div class="w-[92px]">
                        <vx-toggle
                        :disabled="props.disabled"
                            :modelValue="field.description !== null"
                            @update:modelValue="field.description = $event ? '' : null" size="sm"
                        />
                    </div>
                </div>
                <div v-if="field.description !== null">
                    <vx-input v-model="field.description" :disabled="props.disabled" :error="displayFirstError(validation.description)" size="lg" :rows="3" :maxlength="100" />
                </div>
            </div>

            <div class="flex flex-col gap-2" data-test="field-required">
                <div class="flex items-center gap-4">
                    <vx-label class="grow">
                        Required
                        <template v-slot:description> Prevent this field from being left blank. </template>
                    </vx-label>
                    <div class="w-[92px]">
                        <vx-toggle v-model="field.is_required" :disabled="props.disabled" size="sm" />
                    </div>
                </div>
            </div>

            <div class="flex flex-col gap-2" data-test="field-min-length">
                <div class="flex items-center gap-4">
                    <vx-label class="grow"> Minimum Length </vx-label>
                    <div class="w-[92px]">
                        <vx-toggle
                            :modelValue="field.min_length !== null"
                            @update:modelValue="field.min_length = $event ? 1 : null" size="sm"
                            :disabled="props.disabled"
                        />
                    </div>
                </div>
                <div v-if="field.min_length !== null">
                    <vx-input v-model="field.min_length" :disabled="props.disabled" :error="displayFirstError(validation.min_length)" type="number" step="1" size="lg" />
                </div>
            </div>

            <div class="flex flex-col gap-2" data-test="field-max-length">
                <div class="flex items-center gap-4">
                    <vx-label class="grow"> Maximum Length </vx-label>
                    <div class="w-[92px]">
                        <vx-toggle
                            :modelValue="field.max_length !== null"
                            @update:modelValue="field.max_length = $event ? 1000 : null" size="sm"
                            :disabled="props.disabled"
                        />
                    </div>
                </div>
                <div v-if="field.max_length !== null" >
                    <vx-input v-model="field.max_length" :disabled="props.disabled" :error="displayFirstError(validation.max_length)" type="number" step="1" size="lg" />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { faChevronDown, faChevronUp, faTrash } from '@fortawesome/pro-solid-svg-icons';
import useVuelidate from '@vuelidate/core';
import { maxLength, maxValue, minValue, requiredIf } from '@vuelidate/validators';
import { displayFirstError } from '~/utils/validation';
import type { SnippetField } from '~/types/snippet.types';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxLabel, VxButton, VxInput, VxToggle } from '@voxie/frontend-components';
import { ref, computed } from 'vue';
import type { PropType } from 'vue';

const props = defineProps<{ disabled: boolean }>();

const field = defineModel({
    type: Object as PropType<SnippetField>,
    required: true,
});

const emit = defineEmits(['remove']);
const visible = ref(true);

const validation = useVuelidate(
    {
        description: {
            maxLength: maxLength(100),
            requiredIf: requiredIf(()=> field.value.description !== null)
        },
        min_length: {
            min: minValue(1),
            max: maxValue(1000),
            requiredIf: requiredIf(()=> field.value.min_length !== null)
        },
        max_length: {
            min: minValue(1),
            max: maxValue(1000),
            requiredIf: requiredIf(()=> field.value.max_length !== null)
        },
    },
    computed(() => ({
        description: field.value.description,
        min_length: field.value.min_length,
        max_length: field.value.max_length,
    }))
);
</script>
